import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	QueryList,
	ViewChildren,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { ReportSummary, ReportSummaryComment } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { buildSummaryReportTitle } from "src/app/shared/utils/form-type-label";
import { getRankLabel } from "src/app/shared/utils/rank-label.util";
import { HelperService } from "src/app/util/helper.service";
import { SESSION } from "src/session.config";

@Component({
	selector: "app-quick-rating-summary",
	templateUrl: "./quick-rating-summary.component.html",
	styleUrls: ["./quick-rating-summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class QuickRatingSummaryComponent implements OnInit {
	@ViewChildren("summaryReport") summaryReport!: QueryList<ElementRef>;
	@ViewChildren("maneuverBreakdownRef")
	maneuverBreakdownRef!: QueryList<ElementRef>;

	@ViewChildren("instructorNotes") instructorNotes!: QueryList<ElementRef>;
	candidateList: Array<ReportSummary> = [];
	current = 0;
	prev = -1;
	loadingCount: number = 0;

	comments: ReportSummaryComment[] = [];
	otherComments: string[] = [];
	sessionList: string[] = [];

	candidateSignature?: string;
	instructorSignature?: string;

	candidateId?: string;

	routeSub!: Subscription;
	summarySub!: Subscription;

	private _reportSummary?: ReportSummary;
	pdfAttachement: Array<any> = [];
	selectedOBList: any = {};
	observationForm!: UntypedFormGroup;
	meneuverNameList: Array<any> = [];
	obAddRemoveList: Array<any> = [];
	candidateIds: string[] = [];

	constructor(
		private httpService: HttpService,
		private sharedService: SharedService,
		private route: ActivatedRoute,
		public helperService: HelperService,
		private loaderService: LoaderService,
		private fb: UntypedFormBuilder,
		private cdr: ChangeDetectorRef,
		private roleService: RoleService,
	) {}

	setReportSummary(reportSummary: ReportSummary) {
		this.comments = reportSummary?.trainerExaminerNotes?.summary;
		this.otherComments = reportSummary?.trainerExaminerNotes?.otherTrainingAndObservations;

		this.sessionList = reportSummary?.sessionDetails?.sessionDetails ?? [];

		this.candidateSignature = this.helperService.bufferDataToImageUrl(
			reportSummary?.sessionDetails?.traineeSignature?.data,
		);
		this.instructorSignature = this.helperService.bufferDataToImageUrl(
			reportSummary?.sessionDetails?.instructorSignature?.data,
		);

		this._reportSummary = reportSummary;
	}

	get reportSummary(): ReportSummary | undefined {
		return this._reportSummary;
	}

	get summaryReportTitle(): string {
		return buildSummaryReportTitle(this._reportSummary?.formType);
	}

	join(arr?: string[] | { description?: string }[]) {
		if (!arr?.length) return "";
		if (typeof arr[0] === "string") {
			return (arr as string[]).join(", ");
		}
		return (arr as { description?: string }[])
			.map((x) => x?.description ?? "")
			.filter(Boolean)
			.join(", ");
	}

	getRank(rank?: string) {
		return getRankLabel(rank);
	}

	ngOnDestroy(): void {
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}

		if (this.summarySub) {
			this.summarySub.unsubscribe();
		}
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.RATINGS);
		this.loaderService.setLoading(true);

		this.candidateList = [];
		this.candidateId = localStorage.getItem(SESSION.SESSION_KEY_CANDIDATES) ?? "[]";

		const candidates = JSON.parse(this.candidateId);

		this.candidateIds = candidates;

		if (!isEmpty(this.candidateId)) {
			if (candidates.length == 1) {
				this.httpService.getSummary(candidates[0], { role: this.roleService.getUserRole() }).subscribe(
					(resp) => {
						const reportSummary = resp.data as ReportSummary;
						this.candidateList.push(reportSummary);
						this.downloadSummary();
						this.initObservationsForm();
						this.cdr.detectChanges();
						this.loaderService.setLoading(false);
					},
					(err) => {
						this.loaderService.setLoading(false);
					},
				);
			} else if (candidates.length == 2) {
				this.httpService
					.getSummary(candidates[0], { role: this.roleService.getUserRole() })
					.pipe(
						switchMap((resp: any) => {
							this.candidateList = [];
							const reportSummary = resp.data as ReportSummary;
							this.candidateList.push(reportSummary);
							return this.httpService.getSummary(candidates[1], {
								role: this.roleService.getUserRole(),
							});
						}),
					)
					.subscribe(
						(resp) => {
							const reportSummary = resp.data as ReportSummary;
							this.candidateList.push(reportSummary);
							this.downloadSummary();
							this.initObservationsForm();
							this.cdr.detectChanges();
							this.loaderService.setLoading(false);
						},
						(error) => {
							this.loaderService.setLoading(false);
						},
					);
			}
		}
	}

	goBack(stepper: MatStepper) {
		this.gotoTop(stepper, "back");
	}

	goNext(stepper: MatStepper): void {
		this.gotoTop(stepper, "next");
	}

	gotoTop(stepper: MatStepper, direction: string) {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			element.scrollTop = 0;

			if (direction == "next") {
				stepper.next();
			} else if (direction == "back") {
				if (stepper.selectedIndex === 0) {
				} else {
					stepper.previous();
				}
			}
		}, 100);
	}

	onPrev() {
		this.prev = this.current--;
		this.initObservationsForm();
	}

	onNext() {
		this.prev = this.current++;
		this.initObservationsForm();
	}

	isLeftTransition(idx: number): boolean {
		return this.current === idx ? this.prev > this.current : this.prev < this.current;
	}

	formatSummaryDate(dateString?: string): string {
		if (dateString) {
			return moment(dateString).format(CONSTANT.DATE_FORMAT.DISPLAY_DATE_FORMAT);
		} else {
			return "-";
		}
	}

	getPdfName(candidate: ReportSummary) {
		const sessionDetails = candidate?.sessionDetails?.sessionDetails?.join("_");
		const trainingDate = moment(candidate?.trainingDate).format("DDMMYY");
		const name = candidate?.candidateDetails?.name;

		const pdfName = `${sessionDetails}_${name}_${trainingDate}.pdf`;
		return pdfName.toUpperCase();
	}

	downloadSummary() {
		for (const id of this.candidateIds) {
			this.httpService
				.generateSummary(id, {
					downloadOnly: false,
					role: this.roleService.getUserRole(),
				})
				.subscribe(
					(result) => {
						const data = result.data;

						if (this.roleService.isAdmin) {
							const filename = data.report_file_signed_url;
							this.sharedService.download(filename);
						}
					},
					(error) => {
						console.error(error);
					},
				);
		}
	}

	reformatAllOBs(): any {
		let cateogryObj = {} as any;
		let selectedOBList = [] as any;
		let observations = {} as any;
		this.meneuverNameList = [];
		this.obAddRemoveList = [];

		const reportSummary = this.candidateList[this.current] as any;

		console.log(reportSummary);

		reportSummary?.maneuverBreakdown &&
			Object.values(reportSummary?.maneuverBreakdown).map((a, i) => {
				const selectedOBCategory = this.sharedService.organizeManeuvers(a);

				const maneuverCode = reportSummary?.maneuverBreakdown && Object.keys(reportSummary?.maneuverBreakdown)[i];

				const meneuverName = this.sharedService?.dropDownOptionsManeuver?.find(
					({ code }) => code === maneuverCode,
				)?.description;

				this.meneuverNameList.push(meneuverName);
				this.obAddRemoveList.push(a);

				cateogryObj = this.sharedService.generateCategoryObj();
				console.log(selectedOBCategory);
				for (let cat in selectedOBCategory) {
					selectedOBCategory[cat].forEach((item: any, index: number) => {
						const number = parseInt(item.code.split("_")[1]);

						const lastAction =
							item.isRemoved === true
								? CONSTANT.OB_LAST_ACTION.REMOVED
								: item.isOriginal === true
									? item.isAdded === true
										? CONSTANT.OB_LAST_ACTION.ADDED
										: CONSTANT.OB_LAST_ACTION.PRE_SELECTED
									: item.isAdded === true
										? CONSTANT.OB_LAST_ACTION.ADDED
										: CONSTANT.OB_LAST_ACTION.PRE_SELECTED;

						cateogryObj[cat][`${cat}${number}`] = {
							totalCount: item.totalAttempt,
							currentCount: item.activeAttempt,
							preDefined: item.isOriginal,
							lastAction,
						};
					});
				}

				const selectedOB = {
					maneuver: meneuverName,
					maneuverCode: maneuverCode,
					COM: cateogryObj.COM,
					FPA: cateogryObj.FPA,
					KNO: cateogryObj.KNO,
					LTW: cateogryObj.LTW,
					FPM: cateogryObj.FPM,
					PRO: cateogryObj.PRO,
					PSD: cateogryObj.PSD,
					SAW: cateogryObj.SAW,
					WLM: cateogryObj.WLM,
				};

				this.observations.push(this.addOBs());
				console.log(this.observations);
				selectedOBList.push(selectedOB);
			});

		this.selectedOBList = {
			observations: selectedOBList,
		};

		this.observationForm.patchValue(this.selectedOBList);
	}

	initObservationsForm(): any {
		this.observationForm = this.fb.group({
			observations: this.fb.array([]),
		});

		setTimeout(() => {
			this.reformatAllOBs();
		}, 100);
	}

	addOBs(): any {
		const obs = this.fb.group({
			maneuver: ["", []],
			maneuverCode: ["", []],
			PRO: this.fb.group({
				PRO1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PRO7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			COM: this.fb.group({
				COM1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM8: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM9: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				COM10: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			LTW: this.fb.group({
				LTW1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW8: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW9: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW10: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				LTW11: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			PSD: this.fb.group({
				PSD1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD8: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				PSD9: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			FPA: this.fb.group({
				FPA1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPA2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPA3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPA4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPA5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPA6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			FPM: this.fb.group({
				FPM1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				FPM7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			SAW: this.fb.group({
				SAW1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				SAW7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			WLM: this.fb.group({
				WLM1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM8: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				WLM9: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
			KNO: this.fb.group({
				KNO1: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO2: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO3: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO4: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO5: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO6: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
				KNO7: [
					{
						value: {
							totalCount: 0,
							currentCount: 0,
							preDefined: false,
							lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
						},
						disabled: false,
					},
					[],
				],
			}),
		});
		return obs;
	}

	get observations() {
		return this.observationForm.controls["observations"] as UntypedFormArray;
	}

	get maneuver() {
		return this.observations.get("maneuver")!;
	}
}
